import { Injectable, NgZone, inject } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';

import { AppLogger, AppLoggerService } from '../../core/logging';
import { environment } from '../../env';

/** Backoff bounds for reconnecting a dropped stream. */
const INITIAL_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

export interface HouseholdEvent {
  /**
   * SSE event name, e.g. `shopping_list.changed`. The synthetic `open` event
   * is emitted locally on every successful (re)connect so subscribers can
   * refetch to close any gap that opened while disconnected.
   */
  event: string;
  id?: string;
  data: unknown;
}

/**
 * Single multiplexed live stream per household. Opens
 * `GET /households/{id}/events` as Server-Sent Events and emits every event;
 * subscribers demultiplex by the `event` field. One connection carries all
 * household-scoped events (shopping list today, more later) — see the
 * server-driven-events design.
 *
 * Uses `fetch` + a `ReadableStream` reader (not the native `EventSource`) so it
 * can send the OAuth bearer token as an `Authorization` header, reusing the
 * same token the rest of the app uses. The stream auto-reconnects with
 * exponential backoff; unsubscribe to close it.
 */
@Injectable({ providedIn: 'root' })
export class HouseholdEventsService {
  private readonly oauth = inject(OAuthService);
  private readonly zone = inject(NgZone);
  private readonly log: AppLogger = inject(AppLoggerService).createForClass(HouseholdEventsService);

  /**
   * Opens a live stream for the given household. Each subscription owns its own
   * connection; teardown aborts the in-flight fetch and cancels any pending
   * reconnect.
   */
  connect(householdId: number): Observable<HouseholdEvent> {
    return new Observable<HouseholdEvent>(subscriber => {
      let closed = false;
      let running = false;
      let controller: AbortController | null = null;
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
      let retryDelay = INITIAL_RETRY_MS;

      const clearReconnect = () => {
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
      };

      const scheduleReconnect = () => {
        if (closed) return;
        clearReconnect();
        // Don't spin doomed retries while the browser knows it's offline; the
        // `online` handler kicks an immediate reconnect when connectivity is back.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        reconnectTimer = setTimeout(() => void run(), retryDelay);
        retryDelay = Math.min(retryDelay * 2, MAX_RETRY_MS);
      };

      const run = async () => {
        if (closed || running) return;
        running = true;
        controller = new AbortController();
        const url = `${environment.apiUrl}/households/${householdId}/events`;
        try {
          const res = await fetch(url, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${this.oauth.getAccessToken()}`,
              Accept: 'text/event-stream',
            },
            signal: controller.signal,
            cache: 'no-store',
          });

          if (!res.ok || !res.body) {
            this.log.warn?.(`SSE connect failed (${res.status}); will retry`);
            scheduleReconnect();
            return;
          }

          // Connected: reset backoff and let subscribers do an initial refetch.
          retryDelay = INITIAL_RETRY_MS;
          this.emit(subscriber, { event: 'open', data: null });

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            // Frames are separated by a blank line.
            let sep: number;
            while ((sep = buffer.indexOf('\n\n')) !== -1) {
              const frame = buffer.slice(0, sep);
              buffer = buffer.slice(sep + 2);
              const evt = parseFrame(frame);
              if (evt) this.emit(subscriber, evt);
            }
          }

          // Server closed the stream cleanly — reconnect.
          if (!closed) scheduleReconnect();
        } catch (err) {
          if (closed) return; // aborted by our own teardown
          this.log.warn?.('SSE stream error; will reconnect', err);
          scheduleReconnect();
        } finally {
          running = false;
        }
      };

      // Connectivity transitions: drop the dead socket the moment we go offline
      // (and stop retrying), then reconnect the instant we're back — no waiting
      // out the backoff. This is the common case for a shopping view used on the
      // move (wifi ↔ cellular, dead zones, etc.).
      const onOffline = () => {
        clearReconnect();
        controller?.abort();
      };
      const onOnline = () => {
        if (closed || running) return;
        retryDelay = INITIAL_RETRY_MS;
        clearReconnect();
        void run();
      };
      if (typeof window !== 'undefined') {
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
      }

      void run();

      return () => {
        closed = true;
        clearReconnect();
        controller?.abort();
        if (typeof window !== 'undefined') {
          window.removeEventListener('online', onOnline);
          window.removeEventListener('offline', onOffline);
        }
      };
    });
  }

  /**
   * Re-enter Angular's zone before emitting: `fetch` continuations resolve
   * outside the zone, so downstream signal/RxJS updates would otherwise not
   * schedule change detection.
   */
  private emit(subscriber: Subscriber<HouseholdEvent>, evt: HouseholdEvent): void {
    this.zone.run(() => subscriber.next(evt));
  }
}

/**
 * Parse one SSE frame (the text between blank lines) into an event. Returns
 * null for comment-only frames such as the `: ping` keep-alive.
 */
function parseFrame(frame: string): HouseholdEvent | null {
  let event = 'message';
  let id: string | undefined;
  const dataLines: string[] = [];

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (line === '' || line.startsWith(':')) continue; // blank or comment
    const idx = line.indexOf(':');
    const field = idx === -1 ? line : line.slice(0, idx);
    let val = idx === -1 ? '' : line.slice(idx + 1);
    if (val.startsWith(' ')) val = val.slice(1);

    switch (field) {
      case 'event':
        event = val;
        break;
      case 'id':
        id = val;
        break;
      case 'data':
        dataLines.push(val);
        break;
    }
  }

  if (dataLines.length === 0) return null;

  const dataStr = dataLines.join('\n');
  let data: unknown = dataStr;
  try {
    data = JSON.parse(dataStr);
  } catch {
    // Non-JSON payload — hand back the raw string.
  }
  return { event, id, data };
}
